import { useEffect, useMemo } from 'react';
import { useUserStore } from '../stores/userStore';
import { useQuestStore } from '../stores/questStore';
import { useActivityLogStore } from '../stores/activityLogStore';
import { useJournalStore, selectAuthoredEntries } from '../stores/journalStore';
import { useDomainReadiness, useHasLifeMap } from './useDomainReadiness';
import { CATEGORIES } from '../constants/categories';
import { isMappedAndMissioned } from '../constants/domainReadiness';
import { isCompletedAction, hasLinkedCascade } from '../constants/growthJourney';
import { useInboxStore } from '../stores/inboxStore';
import { useScheduleStore } from '../stores/scheduleStore';
import { isRevealed, earnedFrom, REVEALED_BY, completedRevealSteps } from '../constants/moduleReveal';

/**
 * Progressive reveal, as a predicate.
 *
 * ⭐⭐ **EVERY REVEAL SIGNAL HERE COMES FROM A PERSISTED OR LOCAL STORE — THIS
 * HOOK NEVER FETCHES.** It is called from the nav rail, which renders on every
 * screen, so a network call here would run on every app load.
 *
 * Passive signals are sequenced with journeyReachedSteps, the same history
 * used by Home and Roadmap. useJourneySignals banks fetch-backed guild/protocol
 * completions; this hook uses those IDs to bridge steps it cannot observe.
 * It also banks passive in-order completions made elsewhere in the app.
 *
 * ⚠️ Still deliberately NOT the module's full `JourneySignals` — that set
 * includes guilds and protocols, which fetch. Every store read below is
 * passive: subscribing never triggers a load.
 */
export function useReveal(): (surface: string) => boolean {
  const revealedSurfaces = useUserStore(s => s.revealedSurfaces);
  const reached = useUserStore(s => s.journeyReachedSteps);
  const earnJourneySteps = useUserStore(s => s.earnJourneySteps);
  const earnRevealSurfaces = useUserStore(s => s.earnRevealSurfaces);
  const initializedUserId = useUserStore(s => s.initializedUserId);
  const purposeStatement = useUserStore(s => s.purposeStatement);
  const vision = useUserStore(s => s.vision);
  const missionStatement = useUserStore(s => s.missionStatement);
  const moduleAcks = useUserStore(s => s.moduleAcks);
  const quests = useQuestStore(s => s.quests);
  const activityLogs = useActivityLogStore(s => s.logs);
  const journalEntries = useJournalStore(s => s.entries);
  // ⚠️ Added 2026-08-21 — steps 1, 5 and 6 were mapped to surfaces and were not
  // computed at all. Both stores are persisted; subscribing does not fetch.
  const inboxItems = useInboxStore(s => s.items);
  const dayTemplates = useScheduleStore(s => s.templates);

  const hasLifeMap = useHasLifeMap();
  const missions = useUserStore(s => s.missions);
  const readiness = useDomainReadiness(CATEGORIES.map(c => c.id));
  /**
   * ⚠️⚠️ MAPPED + MISSIONED, not activated. This is what reveals the Build page,
   * and counting QUESTS here was a loop: quests are made ON the Build page, so a
   * reveal-enrolled user needed Build to earn Build. See `isMappedAndMissioned`.
   */
  const missionedDomains = CATEGORIES
    .filter(c => isMappedAndMissioned(c.id, readiness, missions)).length;

  const completedSteps = useMemo(() => completedRevealSteps({
    acks: moduleAcks,
    inboxCaptured: inboxItems.length,
    // ⚠️ PROCESSED = out of the funnel. Parked and archived count.
    inboxProcessed: inboxItems.filter(i => i.status !== 'open').length,
    dayTemplates: dayTemplates.length,
    purposeStatement,
    visionStatement: vision,
    missionStatement,
    hasLifeMap,
    missionedDomains,
    // ⚠️ Through the SHARED selector: Generate Actions' outer gate asks the same
    // question, and two copies of this expression would drift.
    hasLinkedCascade: hasLinkedCascade(quests.filter(q => q.status !== 'archived')),
    // ⚠️ `level_up` is not an action anyone took.
    actionsCompleted: activityLogs.filter(l => isCompletedAction(l.activityType)).length,
    // ⚠️ AUTHORED entries; the store seeds 7 permanent demo rows.
    journalEntries: selectAuthoredEntries(journalEntries).length,
  }, reached), [reached, purposeStatement, vision, missionStatement, hasLifeMap, missionedDomains,
      quests, activityLogs, journalEntries, moduleAcks, inboxItems, dayTemplates]);

  // Identity must be loaded before either append-only bank can write. Reveal
  // itself keeps its existing fail-open behaviour while identity is loading.
  const hydrated = initializedUserId !== null;
  const earnedNow = useMemo(() => earnedFrom(completedSteps), [completedSteps]);
  useEffect(() => {
    if (!hydrated) return;
    earnJourneySteps(completedSteps);
    earnRevealSurfaces(earnedNow);
  }, [hydrated, completedSteps, earnedNow, earnJourneySteps, earnRevealSurfaces]);

  return useMemo(() => {
    const state = {
      hydrated,
      earned: revealedSurfaces,
      completedSteps,
    };
    return (surface: string) => isRevealed(surface, state);
  }, [hydrated, revealedSurfaces, completedSteps]);
}

/** Every surface reveal knows about — handy for a Settings preview. */
export const ALL_REVEAL_SURFACES = Object.keys(REVEALED_BY);
